import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  trackCheckInMAEmailMatch,
  trackCheckInMAEmailNoMatch,
  trackCheckInMAEmailGenerated,
} from '../../../analytics';

import { Form, FloatingLabel } from '../../../components/form';
import { Button } from '../../../components/general';
import { ErrorModal } from '../../../components/modals';

import { URL } from '../../../constants';

import { getOneCreation } from '../../../services/api/creations';
import { getFaceByEmail } from '../../../services/api/faces';

import { PublicEvent, ModalError } from '../../../types';
import { goTo, isEmpty, getUrl } from '../../../utils';

interface Props {
  event: PublicEvent;
  reqAssets: string;
}

const MAEmailStage: React.FC<Props> = (props) => {
  const { event, reqAssets } = props;

  const [email, setEmail] = useState('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<ModalError>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsProcessing(true);

    const eventId = event.eventId;

    try {
      const foundFace = await getFaceByEmail(eventId, email, true);

      // if person DIDN'T check in – show error modal
      if (!foundFace) {
        setError({
          heading: 'Hmm, we couldn’t find a check-in with that email',
          subheading:
            'Looks like you might not have checked in during the event. Double-check the email address or try a different one.',
          btn: (
            <div className="w-full flex items-center justify-between gap-4">
              <Button
                onClick={() => goTo('https://crowdclip.com/')}
                className="w-1/2"
              >
                OK :(
              </Button>

              <Button
                onClick={() => setError(null)}
                colour="pink"
                className="w-1/2"
              >
                Try Different Email
              </Button>
            </div>
          ),
        });

        trackCheckInMAEmailNoMatch();

        return;
      }

      const faceId = foundFace.elementId;

      // check if creation is already generated
      const creation = await getOneCreation(faceId);

      // video NOT generated – redirect to corresponding MA page
      if (!creation) {
        goTo(`${getUrl(URL.CHECK_IN, eventId, faceId)}?reqAssets=${reqAssets}`);
        trackCheckInMAEmailMatch();
      }

      // video generated – redirect to share video page
      else {
        navigate(getUrl(URL.SHARE, eventId, faceId));
        trackCheckInMAEmailGenerated();
      }
    } catch {
      setError({
        heading: 'Something went wrong',
        subheading: 'Please try again',
        isTryAgainBtn: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} formClassName="w-full text-center">
      <h5 className="w-full font-normal">
        Missing your personalised video from <b>{event.name}</b>?
      </h5>

      <div className="w-full">
        If you checked in but didn’t receive your video, it might be because
        there wasn’t enough footage of you captured during the event.
      </div>

      <div className="w-full">
        Got moments of yourself on camera? Re-check in below to upload them –
        and we might still be able to make your personalised video.
      </div>

      <div className="font-bold">Tell us the email you checked in with</div>

      <FloatingLabel
        label="Enter your email"
        name="email"
        value={email}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setEmail(e.target.value)
        }
        required
      />

      <Button
        className="w-full"
        disabled={isEmpty(email)}
        isSubmitBtn
        isProcessing={isProcessing}
      >
        Check-in
      </Button>

      <ErrorModal
        show={!!error}
        heading={error?.heading}
        subheading={error?.subheading}
        btn={error?.btn}
        isTryAgainBtn={error?.isTryAgainBtn}
        isContactUs
      />
    </Form>
  );
};

export default MAEmailStage;
